import React, { useEffect, useState } from 'react'
import { CSmartTable, CFormCheck, CFormSelect, CButton, CBadge } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle } from '@coreui/icons'
import ClubService from 'src/service/ClubService'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'

const SEARCH_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '0', label: 'Not Invited' },
  { value: '1', label: 'Invited' },
  { value: '2', label: 'Joined' },
]

// Solid colors (not bg-*-subtle/text-*-emphasis, which render invisible text in this theme).
const SENT_STATUS = {
  0: { label: 'Not Invited', color: 'secondary' },
  1: { label: 'Invited', color: 'info' },
  2: { label: 'Joined', color: 'success' },
}

const MemberInviteUserList = (props) => {
  const [loading, setLoading] = useState()
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('first_name')
  const [statusFilter, setStatusFilter] = useState('')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [users, setUsers] = useState([])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
    setActivePage(1)
  }

  const toggleAllCheckboxes = () => {
    const allIds = Array.isArray(users?.data) ? users.data.map((user) => user?.id) : []
    props?.setSelectedCheckboxes((prevSelected) =>
      prevSelected.length === allIds.length ? [] : allIds,
    )
  }

  // One-way "select all currently displayed" action (distinct from the header checkbox's
  // toggle-on/off), so switching pages doesn't lose people already picked on other pages.
  const selectAllDisplayed = () => {
    const allIds = Array.isArray(users?.data) ? users.data.map((user) => user?.id) : []
    props?.setSelectedCheckboxes((prevSelected) =>
      Array.from(new Set([...prevSelected, ...allIds])),
    )
  }

  const columns = [
    {
      key: 'delete_fixture',
      label: (
        <div className="d-flex align-items-center justify-content-center">
          <CFormCheck
            checked={
              props?.selectedCheckboxes.length > 0 &&
              props?.selectedCheckboxes.length === users?.data?.length
            }
            indeterminate={
              props?.selectedCheckboxes.length > 0 &&
              props?.selectedCheckboxes.length < (users?.data?.length || 0)
            }
            onChange={toggleAllCheckboxes}
          />
        </div>
      ),
      filter: false,
      sorter: false,
      _style: { width: 48 },
      _props: { className: 'ge-invite-check' },
    },
    { label: 'First Name', key: 'first_name', filter: false },
    { label: 'Last Name', key: 'last_name', filter: false },
    { label: 'Email', key: 'email', filter: false },
    { label: 'Created On', filter: false, key: 'created_at' },
    { label: 'Status', filter: false, sorter: true, key: 'is_sent' },
    {
      key: 'actions',
      label: 'Invite',
      filter: false,
      sorter: false,
    },
  ]

  const loadUsers = () => {
    if (!props?.gameId) {
      return
    }

    setLoading(true)
    const offset = itemsPerPage * (activePage - 1)
    const params = new URLSearchParams()

    if (columnFilter && Object.keys(columnFilter).length > 0) {
      Object.keys(columnFilter).forEach((key) => {
        params.append(key, columnFilter[key])
      })
    }
    if (statusFilter !== '') {
      params.append('is_sent', statusFilter)
    }

    if (columnSorter && columnSorter.column) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    ClubService.getUserFromGame(offset, itemsPerPage, activePage, params, props?.gameId)
      .then((result) => {
        setUsers(result.data)
      })
      .catch((error) => {
        console.error('Error fetching users:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePage,
    columnFilter,
    statusFilter,
    columnSorter,
    itemsPerPage,
    props?.gameId,
    props?.reloadToken,
  ])

  const toggleCheckbox = (id) => {
    props?.setSelectedCheckboxes((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id)
      } else {
        return [...prevSelected, id]
      }
    })
  }

  const sendCode = (id) => {
    const payload = { userIds: [id], gameId: props.gameId }
    ClubService.sendMemberInvite(payload).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        loadUsers()
        props?.setSelectedCheckboxes((prev) => prev.filter((selectedId) => selectedId !== id))
      }
    })
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label
  const selectedCount = props?.selectedCheckboxes?.length || 0

  return (
    <>
      <style>{`
        .ge-invite-row.ge-row-selected { background-color: rgba(25,135,84,0.07); }
        .ge-invite-check {
          width: 48px;
          min-width: 48px;
          padding-left: 1.25rem !important;
          padding-right: 0.5rem !important;
          vertical-align: middle !important;
        }
        .ge-invite-check .form-check-input {
          margin: 0;
        }
        .ge-invite-check .form-check-input:checked,
        .ge-invite-check .form-check-input:indeterminate {
          background-color: #198754;
          border-color: #198754;
        }
        .ge-invite-check .form-check-input:focus {
          border-color: #198754;
          box-shadow: 0 0 0 0.2rem rgba(25,135,84,0.25);
        }
      `}</style>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
        trailing={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <CFormSelect
              size="sm"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </CFormSelect>
            <CButton color="secondary" variant="outline" size="sm" onClick={selectAllDisplayed}>
              Select all displayed
            </CButton>
          </div>
        }
      />
      <div className="ge-datatable">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          scopedColumns={{
            delete_fixture: (item) => (
              <th className="ge-invite-check">
                <div className="d-flex align-items-center justify-content-center">
                  <CFormCheck
                    checked={props?.selectedCheckboxes.includes(item.id)}
                    onChange={() => toggleCheckbox(item.id)}
                  />
                </div>
              </th>
            ),
            created_at: (item) => <td> {moment(item.updated_at).format('D.MM.YYYY')}</td>,
            is_sent: (item) => {
              const status = SENT_STATUS[item.is_sent] || SENT_STATUS[0]
              return (
                <td>
                  <CBadge
                    color={status.color}
                    shape="rounded-pill"
                    className="d-inline-flex align-items-center gap-1 px-2 py-1"
                  >
                    {item.is_sent === 2 && <CIcon icon={cilCheckCircle} size="sm" />}
                    {status.label}
                  </CBadge>
                </td>
              )
            },
            actions: (item) => (
              <td>
                <RowActionsMenu
                  actions={[
                    item.is_sent === 0 && {
                      label: 'Send',
                      color: 'success',
                      onClick: () => sendCode(item.id),
                    },
                    item.is_sent === 1 && {
                      label: 'Re-send',
                      color: 'success',
                      onClick: () => sendCode(item.id),
                    },
                    item.is_sent === 2 && { label: 'Joined', disabled: true },
                  ].filter(Boolean)}
                />
              </td>
            ),
          }}
          items={(users?.data || []).map((item) => ({
            ...item,
            _props: {
              className: props?.selectedCheckboxes.includes(item.id)
                ? 'ge-invite-row ge-row-selected'
                : 'ge-invite-row',
            },
          }))}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage: activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(activePage) => setActivePage(activePage)}
          onItemsPerPageChange={(itemsPerPage) => {
            setActivePage(1)
            setItemsPerPage(itemsPerPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
        <div className="fw-semibold">
          {selectedCount} member{selectedCount === 1 ? '' : 's'} selected
        </div>
        {props.footerTrailing}
      </div>
    </>
  )
}

export default MemberInviteUserList
